// Mirror of apps/toolkit-server/src/dashboard/snapshot.ts SalesSnapshot (schema 2.0).
// Keep in sync manually for now; later we may extract @fluxo/dashboard-types.
//
// Lo nuevo de 2.0 va como OPCIONAL: durante un despliegue (o con un snapshot
// congelado) la pantalla puede recibir la forma vieja, y tiene que pintarla. Por
// eso los componentes leen a traves de las funciones de abajo (modulesOf,
// reunionesOf, mensajesOf, personasOf), que reconstruyen lo que falte con lo que
// el propio snapshot lleva, nunca con un perfil por defecto.

/**
 * Mirror of SnapshotModules in the server. Which blocks this tenant contracted,
 * derived from its own config. A block whose module is false is not rendered at
 * all, so a tenant never sees an empty widget belonging to somebody else's setup.
 */
export interface SnapshotModules {
  calls: boolean;
  crm_mirror: boolean;
  crm_provider: 'zoho' | 'hubspot' | 'salesforce' | 'none';
  meeting_targets: boolean;
  /** Solo con Zoho: sin el, el boton escribia en la nada. */
  registrar_reunion?: boolean;
  ganadas?: boolean;
  automatizaciones?: boolean;
  acciones_requeridas?: boolean;
}

export type ModulosPanel = Required<SnapshotModules>;

export interface QuarterlyGoal {
  metric: 'revenue' | 'meetings' | 'calls_connected' | 'opportunities_won' | 'sequences_enrolled';
  user_email: string | null;
  year: number;
  quarter: number;
  target: number;
  currency: string | null;
  achieved: number | null;
  progress_pct: number | null;
  reason?: string;
}

/**
 * The module map of a snapshot, tolerating one that does not carry it (or carries
 * the old four keys).
 *
 * The fallback is reconstructed from the snapshot's OWN content, never from a
 * default profile, so an old payload still describes its own tenant. Las tres
 * preferencias del panel (`ganadas`, `automatizaciones`, `acciones_requeridas`)
 * sin clave quedan como siempre (encendidas); `registrar_reunion` solo con Zoho.
 */
export function modulesOf(s: Snapshot): ModulosPanel {
  const m = s.modules;
  const base = m ?? {
    calls: s.calls?.available === true,
    // `crm_mirror` y `meeting_targets` NO se deducen de las cifras del payload.
    // Se hacia, y era el peor sitio para hacerlo: los payloads congelados antes
    // del 2026-09-26 llevan justo las dos cifras que se retiraron por fabricadas
    // (el objetivo de 60 x N y un `revenue_progress.status: 'ok'` con 0 conseguido
    // porque el CRM no habia contestado), asi que el respaldo encendia los dos
    // bloques usando como prueba lo que se acababa de declarar falso.
    // Sin el mapa de modulos no se sabe, y no saber se pinta como apagado.
    crm_mirror: false,
    crm_provider: 'none' as const,
    meeting_targets: false,
  };
  return {
    calls: base.calls,
    crm_mirror: base.crm_mirror,
    crm_provider: base.crm_provider,
    meeting_targets: base.meeting_targets,
    registrar_reunion: m?.registrar_reunion ?? base.crm_provider === 'zoho',
    ganadas: m?.ganadas ?? true,
    automatizaciones: m?.automatizaciones ?? true,
    acciones_requeridas: m?.acciones_requeridas ?? true,
  };
}

export interface PersonaPanel { id: string; nombre: string; yo: boolean }

export interface FiltroPanel {
  preset: string;
  desde: string;
  hasta: string;
  zona: string;
  personas: 'todas' | string[];
}

export const CATEGORIAS_LLAMADA = ['sin_conversacion', 'negativa', 'interes', 'otro_momento', 'numero_incorrecto', 'interlocutor', 'demo_acordada', 'sin_informacion'] as const;
export type CategoriaLlamada = typeof CATEGORIAS_LLAMADA[number];
export const ETIQUETAS_CATEGORIA_LLAMADA: Record<CategoriaLlamada, string> = {
  sin_conversacion: 'Sin conversación registrada', negativa: 'Conversación negativa', interes: 'Interés o siguiente paso',
  otro_momento: 'Pide otro momento', numero_incorrecto: 'Número incorrecto', interlocutor: 'No se llega al interlocutor',
  demo_acordada: 'Demo acordada', sin_informacion: 'Sin información suficiente',
};
export function categoriaDeLlamada(value: unknown): CategoriaLlamada | undefined {
  return CATEGORIAS_LLAMADA.find((key) => key === value);
}
export interface InsightsLlamadas {
  status: 'ok' | 'unavailable';
  total: number;
  with_result: number;
  with_transcript: number;
  with_objection: number;
  unknown: number;
  distribution: Array<{ key: CategoriaLlamada; label: string; count: number; pct: number | null }>;
}

export type EstadoReunion = 'por_hacer' | 'hecha' | 'no_show' | 'propuesta';

export interface ReunionItem {
  id: string;
  persona_id?: string;
  persona: string;
  titulo: string;
  inicio: string;
  estado: EstadoReunion;
  contactId: string | null;
  /** 'meet': una reunion grabada con Fluxo para Meet sin cita de GHL. */
  fuente: 'ghl' | 'zoho' | 'meet';
  /** Grabada con Fluxo para Meet. */
  grabada?: boolean;
  /** La grabacion: su transcripcion se abre en #/reunion/<id>. */
  grabacionId?: string;
  /** El resumen de la grabacion, si ya esta hecho. */
  resumen?: string | null;
}

export interface CubosReuniones {
  por_hacer: number;
  hechas: number;
  no_show: number;
  /** null: el inquilino no tiene la etapa de propuesta configurada (guion, no 0). */
  propuesta: number | null;
}

export interface MensajesPorPersona { persona: string; enviados: number; personas: number; respondieron: number; tasa: number | null }

export interface BloqueMensajes {
  status: 'ok' | 'unavailable';
  reason?: string;
  total: number;
  sin_verificar?: number;
  personas?: number;
  respondieron?: number;
  tasa?: number | null;
  por_persona?: MensajesPorPersona[];
  /** @deprecated forma vieja: por email. */
  by_rep: Array<{ user_email: string; count: number }>;
}

export interface Snapshot {
  schema_version: string;
  generated_at: string;
  client: { id: string; slug: string; name: string };
  /** Absent only on payloads older than the field; read it through modulesOf(). */
  modules?: SnapshotModules;
  /** Ausente en snapshots anteriores. No reconstruir objetivos desde contadores. */
  quarterly_goals?: QuarterlyGoal[];
  /** El periodo y las personas que de verdad se aplicaron (2.0). */
  filtro?: FiltroPanel;
  /** Las personas que se pueden elegir (2.0). */
  personas?: PersonaPanel[];
  quarter: {
    year: number;
    q: number;
    label: string;
    start: string;
    end: string;
    daysLeft: number;
    daysElapsed: number;
    daysTotal: number;
    progressPct: number;
  };
  reps: Record<string, { email: string; name: string | null; ghl_user_id: string | null; role: string }>;
  revenue_progress: {
    status: 'ok' | 'unavailable';
    reason?: string;
    org?: {
      status: 'ok';
      target_quarter: number;
      target_quarter_prorated: number;
      achieved_quarter: number;
      pct_of_prorated: number;
      currency: string;
    };
  };
  meetings: {
    status?: 'ok' | 'unavailable';
    reason?: string;
    fuente?: 'ghl' | 'ghl_zoho';
    cubos?: CubosReuniones;
    total?: number;
    asistencia?: number | null;
    por_persona?: Array<{ persona: string } & CubosReuniones>;
    items?: ReunionItem[];
    objetivo_medible?: boolean;
    /** null = this tenant has no meetings target configured (never a borrowed default). */
    target: number | null;
    /** ¿El inquilino declaro algun objetivo de reuniones? Hecho suyo, no del lector. */
    objetivo_declarado?: boolean;
    progress_count: number;
    progress_pct: number;
    show_rate: number | null;
    target_breakdown: Array<{ user_email: string; target: number; achieved: number; pct: number }>;
    buckets: { agendadas: number; held: number; noshow: number; toBeHeld: number };
    held_items: Array<{ id: string; rep: string | null; title: string; scheduled_at: string }>;
    scheduled_items: Array<{ id: string; rep: string | null; title: string; scheduled_at: string; status: string; contactId: string | null }>;
    /** @deprecated kept for back-compat */
    all_items: Array<{ id: string; rep: string | null; contact: string | null; company: string | null; scheduled_at: string; status: string | null; title?: string }>;
  };
  lead_demo?: { status: 'ok' | 'unavailable'; reason?: string; leads: number; con_demo: number; hechas: number; tasa: number | null; sin_fecha: number };
  opportunities: {
    summary: { open: number; won_quarter: number; lost_quarter: number; total_open_value: number; won?: number; lost?: number };
    funnel: Array<{ stage_name: string; count: number; cumulative_pct: number; total_value?: number }>;
    velocity: { median_days_to_won: number | null; median_days_in_current_stage: number | null };
    /** Debajo del embudo (7-oct): las objeciones detectadas, los resultados que frenan y las etapas con oportunidades paradas. */
    frenos?: {
      razones: { status: 'ok' | 'unavailable'; reason?: string; total: number; objeciones: Array<{ texto: string; n: number; ultima: string }>; resultados: Array<{ disposition: string; count: number }> };
      cuellos: Array<{ stage_name: string; abiertas: number; paradas: number; mediana_dias: number | null }>;
      threshold_days: number;
    };
  };
  pipeline_card: {
    status: 'ok' | 'unavailable';
    reason?: string;
    pipeline_name: string | null;
    stages: Array<{ stage_id: string; stage_name: string; position: number; open_count: number; total_value: number; is_won: boolean; is_lost: boolean }>;
    totals: { open: number; value: number };
    threshold_days: number;
  };
  calls: {
    available: boolean;
    total_calls: number;
    /** Una categoría por llamada, sobre toda la población del filtro. */
    insights?: InsightsLlamadas;
    connected_calls?: number;
    connected_min: number;
    /** % 0..100; null sin llamadas (2.0). */
    connect_rate: number | null;
    avg_score: number | null;
    by_disposition: Array<{ disposition: string; count: number; pct: number }>;
    por_persona?: Array<{ persona: string; total: number; conectadas: number; por_resultado: Array<{ disposition: string; count: number }> }>;
  };
  relevant_calls: Array<{
    call_id: string;
    contact_name: string | null;
    company: string | null;
    rep: string | null;
    persona?: string;
    score: number | null;
    summary: string | null;
    disposition: string | null;
    started_at: string;
    duration_sec: number;
    audio_path: string | null;
    detail_path: string | null;
    suggested_followup: string | null;
  }>;
  required_actions: Array<{
    kind: string;
    lead: string;
    rep: string | null;
    reason: string;
    last_activity_days: number | null;
    stage: string | null;
    value_eur: number | null;
    suggested_next: string;
    link: string | null;
  }>;
  whatsapp: BloqueMensajes;
  emails: BloqueMensajes;
  /** @deprecated la tarjeta «Automatizaciones» (quitada el 16-oct): el servidor la manda siempre apagada. */
  sequences: { status: 'ok' | 'unavailable'; reason?: string; by_sequence: Array<{ sequence_name: string; active_count: number }>; total_enrolled: number };
  /** La secuencia del seguimiento, solo donde esta encendida (16-oct). Leer con secuenciaOf(). */
  secuencia?: ResumenSecuencia;
  range: { from: string; to: string; preset?: string };
  timelines: {
    calls: Array<{ date: string; total: number; connected: number }>;
    emails: Array<{ date: string; sent: number; replied: number }>;
    whatsapp: Array<{ date: string; sent: number; replied: number }>;
    meetings: Array<{ date: string; scheduled: number; completed: number; por_hacer?: number; hechas?: number; no_show?: number; propuesta?: number }>;
    pipeline: Array<{ date: string; opps_created: number; opps_won: number; value_created: number; value_won: number }>;
  };
  /** Porcentajes 0..100; null sin denominador (2.0). */
  rates: {
    calls_connect_rate: number | null;
    emails_reply_rate: number | null;
    whatsapp_reply_rate: number | null;
    meeting_show_rate: number | null;
    opp_win_rate: number | null;
  };
  /** @deprecated solo en snapshots anteriores a 2.0: la tarjeta fija de "Hoy" ya no existe. */
  today?: { date: string; calls: number; emails: number; whatsapp: number };
}

/**
 * LA SECUENCIA EN EL PANEL (16-oct; S/seguimiento/secuencia-resumen.ts): cifras sumadas de la vista
 * «Secuencia», sin filas ni nombres. Lo de AHORA: no sigue al periodo ni a las personas.
 */
export interface ToquesSecuencia { llamada: number; email: number; whatsapp: number }
export interface ResumenSecuencia {
  /** Los dias de la ventana (90): las cerradas que cuentan y la base de «Respondieron». */
  ventanaDias: number;
  /** Los ciclos de la secuencia (8). */
  ciclos: number;
  cifras: {
    /** Las personas dentro ahora. */
    enSecuencia: number;
    /** Las personas que entraron en la ventana (dentro y fuera). */
    entraron: number;
    /** Las de dentro que esperan tu llamada; con retraso, desde 2 dias habiles; la mas antigua, en dias. */
    esperan: { n: number; conRetraso: number; masAntigua: number | null };
    /** Las de dentro con mas de 21 dias en la secuencia (20-oct); sin el, un servidor de antes. Los anillos no lo usan. */
    fueraDePlazo?: number;
    /** Los toques hechos de las que entraron en la ventana. */
    toques: ToquesSecuencia;
    /** Las que salieron dando señal (respondio, te llamo, cogio, reunion, baja), con el canal. */
    respondieron: { n: number; porCanal: ToquesSecuencia };
  };
  /** Ciclos completos sumados de las personas dentro (su base: ciclos x enSecuencia). */
  ciclosHechos: number;
  /** Medianas en dias de las personas dentro; la del deal, solo de las `conDeal` que tienen oportunidad. */
  medianaDiasSecuencia: number | null;
  medianaDiasDeal: number | null;
  conDeal: number;
  /** toques / entraron, con un decimal. */
  toquesPorPersona: number | null;
}

const entero = (n: unknown): n is number => Number.isSafeInteger(n) && (n as number) >= 0;
const numeroONulo = (n: unknown): n is number | null => n === null || (typeof n === 'number' && Number.isFinite(n) && n >= 0);
const objeto = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);
function toquesDe(x: unknown): ToquesSecuencia | null {
  return objeto(x) && entero(x.llamada) && entero(x.email) && entero(x.whatsapp) ? { llamada: x.llamada, email: x.email, whatsapp: x.whatsapp } : null;
}

/**
 * El bloque `secuencia` del snapshot, solo si es entero y cuadra (las de dentro no son mas que
 * las que entraron, las que esperan no son mas que las de dentro, los ciclos no pasan de su base...).
 * Ausente o roto: undefined, y el panel no lo pinta (nunca un anillo con cifras a medias).
 */
export function secuenciaOf(s: Snapshot): ResumenSecuencia | undefined {
  const r: unknown = (s as { secuencia?: unknown }).secuencia;
  if (!objeto(r) || !objeto(r.cifras)) return undefined;
  const c = r.cifras;
  const esperan = objeto(c.esperan) ? c.esperan : null;
  const respondieron = objeto(c.respondieron) ? c.respondieron : null;
  const toques = toquesDe(c.toques);
  const porCanal = toquesDe(respondieron?.porCanal);
  if (!entero(r.ventanaDias) || r.ventanaDias === 0 || !entero(r.ciclos) || r.ciclos === 0) return undefined;
  if (!entero(c.enSecuencia) || !entero(c.entraron) || c.enSecuencia > c.entraron || !toques || !porCanal) return undefined;
  if (!esperan || !entero(esperan.n) || !entero(esperan.conRetraso) || esperan.conRetraso > esperan.n || esperan.n > c.enSecuencia) return undefined;
  if (!(esperan.masAntigua === null || entero(esperan.masAntigua)) || (esperan.n > 0 && esperan.masAntigua === null)) return undefined;
  if (!respondieron || !entero(respondieron.n) || respondieron.n > c.entraron) return undefined;
  if (!entero(r.ciclosHechos) || r.ciclosHechos > r.ciclos * c.enSecuencia) return undefined;
  if (!entero(r.conDeal) || r.conDeal > c.enSecuencia) return undefined;
  if (!numeroONulo(r.medianaDiasSecuencia) || !numeroONulo(r.medianaDiasDeal) || !numeroONulo(r.toquesPorPersona)) return undefined;
  return {
    ventanaDias: r.ventanaDias, ciclos: r.ciclos,
    cifras: {
      enSecuencia: c.enSecuencia, entraron: c.entraron,
      esperan: { n: esperan.n, conRetraso: esperan.conRetraso, masAntigua: esperan.masAntigua as number | null },
      ...(entero(c.fueraDePlazo) && c.fueraDePlazo <= c.enSecuencia ? { fueraDePlazo: c.fueraDePlazo } : {}),
      toques, respondieron: { n: respondieron.n, porCanal },
    },
    ciclosHechos: r.ciclosHechos, medianaDiasSecuencia: r.medianaDiasSecuencia, medianaDiasDeal: r.medianaDiasDeal,
    conDeal: r.conDeal, toquesPorPersona: r.toquesPorPersona,
  };
}

// ── Lectura tolerante (2.0 o anterior) ───────────────────────────────────────

/** El nombre de una persona a partir de un email de la forma vieja: su nombre si se sabe, si no la parte local. */
function nombreDeEmailViejo(s: Snapshot, email: string | null | undefined): string {
  if (!email) return 'Sin asignar';
  const r = s.reps?.[email];
  return r?.name?.trim() || email.split('@')[0] || email;
}

export interface Reuniones {
  status: 'ok' | 'unavailable';
  reason?: string;
  cubos: CubosReuniones;
  total: number;
  asistencia: number | null;
  items: ReunionItem[];
  por_persona: Array<{ persona: string } & CubosReuniones>;
}

/**
 * Las reuniones del periodo. De un snapshot viejo (held / toBeHeld / noshow) se
 * reconstruyen los mismos cubos sin inventar la propuesta (null).
 */
export function reunionesOf(s: Snapshot): Reuniones {
  const m = s.meetings;
  if (m.cubos && m.items) {
    return {
      status: m.status ?? 'ok', reason: m.reason, cubos: m.cubos, total: m.total ?? m.items.length,
      asistencia: m.asistencia ?? null, items: m.items, por_persona: m.por_persona ?? [],
    };
  }
  const ahora = Date.now();
  const items: ReunionItem[] = [
    ...(m.held_items ?? []).map((h) => ({ id: `z-${h.id}`, persona: nombreDeEmailViejo(s, h.rep), titulo: h.title, inicio: h.scheduled_at, estado: 'hecha' as const, contactId: null, fuente: 'zoho' as const })),
    ...(m.scheduled_items ?? []).flatMap((x): ReunionItem[] => {
      const pasada = Date.parse(x.scheduled_at) < ahora;
      const estado: EstadoReunion | null = x.status === 'noshow' || x.status === 'cancelled' || (x.status === 'scheduled' && pasada) ? 'no_show' : x.status === 'scheduled' ? 'por_hacer' : null;
      return estado ? [{ id: x.id, persona: nombreDeEmailViejo(s, x.rep), titulo: x.title, inicio: x.scheduled_at, estado, contactId: x.contactId, fuente: 'ghl' }] : [];
    }),
  ].sort((a, b) => b.inicio.localeCompare(a.inicio));
  const b = m.buckets ?? { held: 0, toBeHeld: 0, noshow: 0, agendadas: 0 };
  const cubos: CubosReuniones = { por_hacer: b.toBeHeld, hechas: b.held, no_show: b.noshow, propuesta: null };
  // Sin reparto por persona: las listas viejas no siempre suman los cubos (venian de
  // fuentes distintas), y un grafico que no cuadra con su tarjeta es peor que uno solo.
  return {
    status: 'ok', cubos, total: b.held + b.toBeHeld + b.noshow,
    asistencia: b.held + b.noshow > 0 ? b.held / (b.held + b.noshow) : null,
    items, por_persona: [],
  };
}

/** Los enviados por persona (correos o WhatsApp). De la forma vieja: por email, sin respondieron. */
export function mensajesOf(s: Snapshot, canal: 'emails' | 'whatsapp'): { total: number; por_persona: MensajesPorPersona[]; personas: number | null; respondieron: number | null; tasa: number | null; sin_verificar: number } {
  const b = s[canal];
  if (b.por_persona) {
    return { total: b.total, por_persona: b.por_persona, personas: b.personas ?? null, respondieron: b.respondieron ?? null, tasa: b.tasa ?? null, sin_verificar: b.sin_verificar ?? 0 };
  }
  return {
    total: b.total,
    por_persona: (b.status === 'ok' ? b.by_rep : []).map((r) => ({ persona: nombreDeEmailViejo(s, r.user_email), enviados: r.count, personas: 0, respondieron: 0, tasa: null })),
    personas: null, respondieron: null, tasa: null, sin_verificar: 0,
  };
}

/** El nombre de quien hizo una llamada relevante. */
export function personaDeLlamada(s: Snapshot, c: Snapshot['relevant_calls'][number]): string {
  return c.persona ?? nombreDeEmailViejo(s, c.rep);
}

/**
 * Etiquetas cortas y UNICAS para los ejes de los graficos: el nombre de pila si no
 * se repite; si se repite, nombre y ultimo apellido ("Joan Fluxo", "Joan Chova");
 * y si aun asi se repite, el nombre entero. "Sin asignar" va siempre tal cual.
 */
export function etiquetasCortas(nombres: readonly string[]): Map<string, string> {
  const partes = (n: string) => n.trim().split(/\s+/);
  const niveles: Array<(n: string) => string> = [
    (n) => partes(n)[0] ?? n,
    (n) => { const p = partes(n); return p.length > 1 ? `${p[0]} ${p[p.length - 1]}` : n; },
    (n) => n.trim(),
  ];
  const unicos = [...new Set(nombres)];
  const out = new Map<string, string>();
  for (const n of unicos) {
    if (n === 'Sin asignar') { out.set(n, n); continue; }
    for (const nivel of niveles) {
      const etiqueta = nivel(n);
      const choca = unicos.some((m) => m !== n && m !== 'Sin asignar' && nivel(m) === etiqueta);
      if (!choca || nivel === niveles[niveles.length - 1]) { out.set(n, etiqueta); break; }
    }
  }
  return out;
}

/** "7 sep - 6 oct" (o "6 oct" si es un solo dia), del periodo que aplico el servidor. */
export function rotuloPeriodo(desde: string, hasta: string): string {
  const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const d = (x: string) => { const [y, m, dd] = x.split('-').map(Number); return { y, txt: `${dd} ${MESES[(m ?? 1) - 1]}` }; };
  const a = d(desde); const b = d(hasta);
  if (desde === hasta) return b.txt;
  return a.y === b.y ? `${a.txt} - ${b.txt}` : `${a.txt} ${a.y} - ${b.txt} ${b.y}`;
}
